import React, { lazy, Suspense, memo, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ConfigProvider } from "antd";
import { useTranslation } from "react-i18next";
import Loading from "components/shared-components/Loading";
import { getLightTheme, darkTheme, baseTheme } from "configs/ThemeConfig";
import { resources } from "lang";
import useBodyClass from "utils/hooks/useBodyClass";
import Routes from "routes";
import { CustomThemeContext } from "../components/util-components/CustomThemeContext";
import LiveChat from "../components/shared-components/LiveChat";
import { apiLocaleToKey, LANGUAGES, changeAppLocale } from "../components/shared-components/LanguageSwitcher";

const BROWSER_LOCALE_DETECTED_KEY = "browser_locale_detected";

const AppLayout = lazy(() => import("./AppLayout"));
const AuthLayout = lazy(() => import("./AuthLayout"));
const BrandStoreLayout = lazy(() => import("./BrandStoreLayout"));
const CatalogLayout = lazy(() => import("./CatalogLayout"));

const Layouts = () => {
  const [primaryColor, setPrimaryColor] = useState(baseTheme.colorPrimary);
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);
  const blankLayout = useSelector((state) => state.theme.blankLayout);
  const isBrandStore = useSelector((state) => state.theme.isBrandStore);
  const isCatalog = useSelector((state) => state.theme.isCatalog);

  // Layout selection logic
  let Layout;
  if (isCatalog) {
    Layout = CatalogLayout;
  } else if (isBrandStore) {
    Layout = BrandStoreLayout;
  } else if (token && !blankLayout) {
    Layout = AppLayout;
  } else {
    Layout = AuthLayout;
  }

  const { i18n } = useTranslation();
  const locale = i18n.language || 'en';

  // Sync i18n with backend user.locale on login (and any change from server)
  useEffect(() => {
    if (!user?.locale) return;
    const desiredKey = apiLocaleToKey(user.locale);
    if (desiredKey && i18n.language !== desiredKey) {
      i18n.changeLanguage(desiredKey);
      localStorage.setItem('app_language', desiredKey);
    }
  }, [user?.locale, i18n]);

  // First-time login: if user.locale is the default (en_US) and the browser
  // language matches another supported locale, switch to it once.
  // Per i18n integration guide §5.3 — runs only once per browser.
  useEffect(() => {
    if (!user || user.locale !== "en_US") return;
    if (localStorage.getItem(BROWSER_LOCALE_DETECTED_KEY)) return;

    const browserLang = navigator.language?.split("-")[0];
    if (!browserLang || browserLang === "en") {
      localStorage.setItem(BROWSER_LOCALE_DETECTED_KEY, "1");
      return;
    }

    const matched = LANGUAGES.find((l) => l.key === browserLang);
    if (!matched?.apiCode) {
      localStorage.setItem(BROWSER_LOCALE_DETECTED_KEY, "1");
      return;
    }

    localStorage.setItem(BROWSER_LOCALE_DETECTED_KEY, "1");
    changeAppLocale(matched.key, { i18n, dispatch, isLoggedIn: true, reloadAfter: false });
  }, [user, i18n, dispatch]);

  const direction = useSelector((state) => state.theme.direction);

  const currentTheme = useSelector((state) => state.theme.currentTheme);

  const currentAppLocale = resources[locale] || resources['en'];

  useBodyClass(`dir-${direction}`);

  const themeConfig =
    currentTheme === "light"
      ? { ...getLightTheme(primaryColor) }
      : { ...darkTheme };

  return (
    <CustomThemeContext.Provider value={{ primaryColor, setPrimaryColor }}>
      <ConfigProvider
        theme={themeConfig}
        direction={direction}
        locale={currentAppLocale.antd}
      >
        {/* <LiveChat user={token ? user : null} /> */}
        <Suspense fallback={<Loading cover="content" />}>
          <Layout>
            <Routes />
          </Layout>
        </Suspense>
      </ConfigProvider>
    </CustomThemeContext.Provider>
  );
};

export default memo(Layouts);
